/**
 * Partner billing SETUP screen. Shown when a firm member enters the portal but the FIRM hasn't
 * chosen a billing option yet (server reports summary.mode === 'none'), and billing is live.
 *
 * The firm picks ONE of two ways to pay (the client never pays the adviser through the app):
 *   • Annual plan   — $800+GST/yr, includes 20 clients (then $70 each). A Stripe subscription.
 *   • Pay per client — $336+GST per client. Choosing this charges the FIRST client now; each later
 *                      client is charged when they take it on.
 * Only the firm owner can do this (server also enforces owner + aal2). Safe while billing is off —
 * the server reports mode null and this screen never appears.
 */
import React, { useState } from 'react';
import { Building2, Check, ArrowRight, ShieldCheck, AlertCircle, LogOut, CalendarClock, UserPlus } from 'lucide-react';
import { startCheckout, type CheckoutPlan } from '../../services/billingApi.ts';

const ANNUAL_POINTS = [
  'Up to 20 active clients included',
  'Then just $70 + GST per extra client',
  'Best value once you have a handful of clients',
];
const PAYG_POINTS = [
  'Pay only for the clients you take on',
  'No yearly commitment',
  'Switch to the annual plan any time',
];

interface PartnerSubscribeProps {
  firmName?: string;
  tenantId?: string | null;
  onSignOut?: () => void;
}

const PartnerSubscribe = ({ firmName, tenantId, onSignOut }: PartnerSubscribeProps) => {
  const [busy, setBusy] = useState<CheckoutPlan | null>(null); // 'partner' | 'partner_client' | null
  const [error, setError] = useState<string | null>(null);

  const choose = async (plan: CheckoutPlan) => {
    setBusy(plan);
    setError(null);
    try {
      const res = await startCheckout(plan, tenantId);
      if (!res.ok) {
        setError(res.message || 'Could not start the payment page. Please try again.');
        setBusy(null);
      }
    } catch {
      setError('We couldn’t reach the payment service. Please try again in a moment.');
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-6 pt-7 pb-8 text-center text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
            <Building2 className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold">Choose how {firmName || 'your firm'} pays</h1>
          <p className="mt-1 text-sm text-white/85">Pick the option that suits you — you can change later</p>
        </div>

        <div className="p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Annual plan */}
            <div className="flex flex-col rounded-2xl border-2 border-[#1a6fd4]/30 bg-white p-5">
              <div className="mb-1 flex items-center gap-2 text-[#1a6fd4]">
                <CalendarClock size={18} />
                <span className="text-sm font-semibold uppercase tracking-wide">Annual plan</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">$800<span className="text-base font-semibold text-slate-500"> + GST</span></div>
              <div className="mb-3 text-sm text-slate-500">per year</div>
              <ul className="mb-4 space-y-2 text-sm text-slate-700">
                {ANNUAL_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><Check size={12} /></span>
                    {p}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => choose('partner')}
                disabled={busy !== null}
                className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-3 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:brightness-[1.04] disabled:opacity-50"
              >
                {busy === 'partner' ? 'Taking you to payment…' : (<>Choose annual <ArrowRight size={16} /></>)}
              </button>
            </div>

            {/* Pay per client */}
            <div className="flex flex-col rounded-2xl border border-slate-200 p-5">
              <div className="mb-1 flex items-center gap-2 text-slate-600">
                <UserPlus size={18} />
                <span className="text-sm font-semibold uppercase tracking-wide">Pay per client</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">$336<span className="text-base font-semibold text-slate-500"> + GST</span></div>
              <div className="mb-3 text-sm text-slate-500">per client</div>
              <ul className="mb-4 space-y-2 text-sm text-slate-700">
                {PAYG_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Check size={12} /></span>
                    {p}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => choose('partner_client')}
                disabled={busy !== null}
                className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-[#1a6fd4] py-3 font-semibold text-[#1a6fd4] transition hover:bg-blue-50 disabled:opacity-50"
              >
                {busy === 'partner_client' ? 'Taking you to payment…' : (<>Start with 1 client <ArrowRight size={16} /></>)}
              </button>
            </div>
          </div>

          <p className="mt-3 text-center text-xs text-slate-400">
            Choosing “pay per client” charges for your first client now; you’re only charged again when you take on another.
          </p>

          {error && (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={13} /> Secure payment by Stripe · Cancel any time from your billing portal
          </div>

          {onSignOut && (
            <button type="button" onClick={onSignOut} className="mt-3 flex w-full items-center justify-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-700">
              <LogOut size={14} /> Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerSubscribe;
